import { observer } from "mobx-react";
import React from "react";
import { useDrag, useDrop } from "react-dnd";
import { ImageTypeEnum } from "../../../../constants/enums/image-type-enum";

const ImageCard = (props: any) => {
  const {
    id,
    index,
    images,
    handleDeleteImage,
    handleShowPreview,
    handleShowEditor,
    viewOnly,
    children,
    imageType
  } = props;

  const onMouseEnter = (e: any) => {
    const overlay = e.currentTarget.querySelector(
      ".image-card-overlay",
    ) as HTMLElement | null;
    if (overlay) overlay.style.opacity = "1";
  };

  const onMouseLeave = (e: any) => {
    const overlay = e.currentTarget.querySelector(
      ".image-card-overlay",
    ) as HTMLElement | null;
    if (overlay) overlay.style.opacity = "0";
  };

  const imageUrl = index === -1 ? images[0] : images[index];

  return (
    <div
      key={id}
      className="image-card-container"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {imageUrl ? (
        <>
          <img
            src={imageUrl}
            alt={`image ${index + 1}`}
            className="image-card-img"
          />
          <div className="image-card-overlay">
            {/* Eye icon */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="white"
              width="20"
              height="20"
              className="image-card-icon"
              onClick={() => handleShowPreview(index, imageType)}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            {/* Pencil icon */}
            {
              !viewOnly &&
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="white"
                  width="20"
                  height="20"
                  className="image-card-icon"
                  onClick={() => handleShowEditor(index, imageType)}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16.862 3.487a2.25 2.25 0 113.182 3.182L7.5 19.313 3 21l1.688-4.5L16.862 3.487z"
                  />
                </svg>
                {/* Trash icon */}
                {
                  handleDeleteImage &&
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="white"
                    width="20"
                    height="20"
                    className="image-card-icon"
                    onClick={() => handleDeleteImage(index, imageType)}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                }
              </>
            }
          </div>
        </>
      ) : (
        <span className="image-card-placeholder">{children}</span>
      )}
    </div>
  );
};

interface ImageCardDnDProps {
  id: string;
  type: string;
  index: number;
  moveCard: (from: number, to: number) => void;
  images: string[];
  handleDeleteImage?: (indexToDelete: number) => void;
  handleShowPreview: (index: number, imageType: ImageTypeEnum) => void;
  handleShowEditor: (index: number, imageType: ImageTypeEnum) => void;
  viewOnly:boolean;
  children?: any;
  imageType: ImageTypeEnum;
}


const ImageCardDnD: React.FC<ImageCardDnDProps> = ({
  id,
  type,
  index,
  moveCard,
  images,
  handleDeleteImage,
  handleShowPreview,
  handleShowEditor,
  viewOnly = false,
  children,
  imageType
}) => {
  const isTemplate = imageType === ImageTypeEnum.TemplateBefore || imageType === ImageTypeEnum.TemplateAfter;
  const ref = React.useRef<HTMLDivElement>(null);
  const hasImage = Boolean(images[index]);

  const [, drop] = useDrop({
    accept: type,
    canDrop: () => !isTemplate && !viewOnly && hasImage,
    drop(item: { index: number }) {
      const from = item.index;
      const to = index;
      if (from !== to && hasImage && images[from]) {
        moveCard(from, to);
        item.index = to;
      }
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type,
    item: { index },
    canDrag: () => !isTemplate && !viewOnly && hasImage,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // Only apply drag-drop when not viewOnly
  if (!viewOnly) {
    drag(drop(ref));
  }

  return (
    <div ref={ref} style={{ opacity: isDragging ? 0.5 : 1 }}>
      <ImageCard
        id={id}
        index={index}
        images={images}
        handleDeleteImage={handleDeleteImage}
        handleShowPreview={handleShowPreview}
        handleShowEditor={handleShowEditor}
        viewOnly={viewOnly}
        imageType={imageType}
      >
        {children || "Empty"}
      </ImageCard>
    </div>
  );
};

export default observer(ImageCardDnD);

// export default ImageCard;
